/**
 * worlds_view — planet kinds, moons, small bodies and system-plane discs (the natural object layer, 2026-10-07).
 *
 * The makeup (system_makeup.ts) says what a system holds and where; this view DRAWS it:
 *  - `planetLook(i)` — what planet i wears: its kind's face, tint, air, shell and surface terms (world_look.
 *    planetKindSpec → planet_look.planetMaterial's `surface`).
 *  - `attachPlanet(i, h)` — what hangs on it: an escaping envelope's tail (away from the star), a forming giant's own
 *    disc, its moons (moon_view) and the plasma torus a volcanic moon feeds.
 *  - `dressRock` / `build` / `frame` — the small bodies and discs (comet_view, smallbody_view, disc_system).
 * Everything translucent stands on a body layer (world_layers.ts); every moving body moves on the wall clock.
 */
import * as THREE from 'three/webgpu';
import type { NaturalEnv, WorldsView, PlanetHandle, PlanetKindLook } from './natural_api.js';
import type { SystemMakeup, PlanetMakeup } from './natural_types.js';
import { uPlanetSun } from '../planet_look.js';
import { derivedRingFor } from '../rings.js';
import { deferDisposeTree } from '../../defer_dispose.js';
import { planetKindSpec, evapTailSpec, circumplanetarySpec, plasmaTorusSpec, type PlanetKindSpec } from './world_look.js';
import { buildTail, buildPlaneDisc, type TailHandle } from './world_layers.js';
import { buildMoons, catalogFacesOn, smallFaceState, type MoonSet } from './moon_view.js';
import { faceByName, planetFaceTex } from './face_catalog.js';
import { BeltLooks, makeRockKit, buildSmallBodies, type SmallBodies } from './smallbody_view.js';
import { buildComets, type CometSet } from './comet_view.js';
import { buildDiscs, type DiscSet } from './disc_system.js';

interface Attached {
  index: number; pm: PlanetMakeup; h: PlanetHandle;
  tail: TailHandle | null; disc: boolean; torus: boolean; moons: MoonSet | null;
  own: THREE.Object3D[];
}

class Worlds implements WorldsView {
  /** This view draws the system's comets (known before anything is built: the sector asks while it lays its own points of interest). */
  readonly comets: boolean;
  private readonly belts: BeltLooks;
  private small: SmallBodies | null = null;
  private cometSet: CometSet | null = null;
  private discs: DiscSet | null = null;
  private readonly specs = new Map<number, PlanetKindSpec>();
  private readonly attached: Attached[] = [];
  private disposed = false;
  private readonly sun = new THREE.Vector3();
  /** `atlas:planet-faces = 'off'` → the five pooled faces alone (read once per sector visit). */
  private readonly faces = catalogFacesOn();

  constructor(private readonly env: NaturalEnv, private readonly makeup: SystemMakeup) {
    this.comets = makeup.features.some((f) => f.kind === 'comet');
    this.belts = new BeltLooks(makeup.fields, makeup.systemId);
  }

  private spec(index: number): PlanetKindSpec | null {
    const pm = this.makeup.planets[index];
    if (!pm) return null;
    let s = this.specs.get(index);
    if (!s) { s = planetKindSpec(pm, this.makeup.systemId, { faces: this.faces }); this.specs.set(index, s); }
    return s;
  }

  planetLook(index: number): PlanetKindLook | null {
    const s = this.spec(index);
    if (!s) return null;
    const row = s.faceKey ? faceByName(s.faceKey) : undefined;
    return {
      face: s.face,
      faceTex: row ? planetFaceTex(row) : null,
      opts: { rim: s.rim, atmosphere: s.atmosphere, ocean: s.ocean, tint: s.tint, surface: s.surface },
      shell: s.shell ? { hue: s.shell.hue, extra: { ...s.shell.extra } } : null,
      spin: s.spin,
    };
  }

  attachPlanet(index: number, h: PlanetHandle): void {
    const pm = this.makeup.planets[index];
    if (!pm || this.disposed) return;
    const env = this.env;
    const a: Attached = { index, pm, h, tail: null, disc: false, torus: false, moons: null, own: [] };
    env.sunAt(h.abs, this.sun);
    const sunDir = h.look?.sunDir ?? { value: this.sun.clone() };
    // PL-15: the escaping envelope streams AWAY from the star and trails the orbit a little
    if (pm.evaporating) {
      const ts = evapTailSpec(pm.kind, pm.seed);
      const tail = buildTail({ profile: 'gas', color: ts.color, seed: pm.seed, planetR: h.radius, g: ts.dust ? 0.4 : 0, segs: 44, across: 16 });
      tail.axis.copy(this.sun).negate();
      tail.sun.copy(this.sun);
      tail.lag.set(0, 1, 0).cross(tail.axis).normalize();            // against a prograde orbit in the system plane
      if (tail.lag.lengthSq() < 0.5) tail.lag.set(1, 0, 0);
      // it begins 1.8 radii UPSTREAM: the head of the cloud holds the planet (world_layers' gas profile reads the negative start)
      tail.set(-1.8 * h.radius, (ts.lengthR + 1.8) * h.radius, 0.22, ts.widthR * h.radius, ts.widthR * h.radius * 2.1, ts.gain);
      for (const m of tail.meshes) { h.group.add(m); a.own.push(m); }
      a.tail = tail;
    }
    // NE-09: a forming giant's own disc — in its ring's plane when the sector also draws it a ring
    if (pm.circumplanetary) {
      const ds = circumplanetarySpec(pm.seed);
      const ring = pm.ringed ? derivedRingFor(this.makeup.systemId, index, pm.face, { x: h.abs.x, y: h.abs.y, z: h.abs.z }) : null;
      const normal = ring ? ring.normal : { x: Math.sin(ds.tilt) * Math.sin(ds.node), y: Math.cos(ds.tilt), z: -Math.sin(ds.tilt) * Math.cos(ds.node) };
      const d = buildPlaneDisc({ kind: 'circumplanetary', R: h.radius, rIn: ring ? ring.rOut * 1.02 : ds.rIn, rOut: ring ? ring.rOut * 1.02 + (ds.rOut - ds.rIn) : ds.rOut, normal, sunColor: uPlanetSun, sunDir, color: ds.color, warm: ds.warm, tau: ring ? ds.tau * 0.5 : ds.tau });
      h.group.add(d.back, d.front); a.own.push(d.back, d.front);
      a.disc = true;
    }
    if (pm.moons.length) {
      a.moons = buildMoons(env, h, pm.moons, `${this.makeup.systemId}:${index}`, undefined, { faces: this.faces });
      // NE-21: the plasma torus a volcanic moon feeds, in that moon's own plane
      const vm = pm.moons.find((m) => m.kind === 'volcanic');
      if (vm && pm.aurora) {
        const ts = plasmaTorusSpec(vm.orbitM / h.radius);
        const si = Math.sin(vm.inc), ci = Math.cos(vm.inc);
        const d = buildPlaneDisc({ kind: 'torus', R: h.radius, rIn: ts.r - ts.width, rOut: ts.r + ts.width, normal: { x: si * Math.sin(vm.node), y: ci, z: -si * Math.cos(vm.node) }, sunColor: uPlanetSun, sunDir, color: ts.color, gain: ts.gain });
        h.group.add(d.back, d.front); a.own.push(d.back, d.front);
        a.torus = true;
      }
    }
    this.attached.push(a);
  }

  dressRock(field: number, i: number, rock: THREE.Object3D): void {
    if (!this.disposed) this.belts.dress(field, i, rock);
  }

  build(rock: THREE.Object3D): void {
    if (this.disposed || this.small) return;
    const kit = makeRockKit(rock);
    this.belts.finish(kit); // ONE material per belt, now that the master is known
    this.small = buildSmallBodies(this.env, this.makeup, this.makeup.systemId, kit, this.belts);
    this.cometSet = buildComets(this.env, this.makeup, this.makeup.systemId, kit);
    this.discs = buildDiscs(this.env, this.makeup);
  }

  frame(_dt: number, nowS: number): void {
    if (this.disposed) return;
    for (const a of this.attached) a.moons?.frame(nowS);
    this.small?.frame(nowS);
    this.cometSet?.frame(nowS);
  }

  state(): Record<string, unknown> {
    return {
      system: this.makeup.systemId, level: this.env.level, faces: this.faces, moonFaces: { loaded: [...smallFaceState.loaded], fellBack: [...smallFaceState.fellBack], failed: [...smallFaceState.failed] }, comets: this.cometSet?.state() ?? null, discs: this.discs?.state() ?? [], small: this.small?.state() ?? { belts: this.belts.state() },
      planets: this.makeup.planets.map((pm, i) => {
        const s = this.spec(i), a = this.attached.find((x) => x.index === i);
        return { kind: pm.kind, variant: s?.variant ?? '', face: s?.face ?? -1, faceKey: s?.faceKey ?? null, shell: !!s?.shell, tail: a?.tail ? a.tail.state() : null, disc: !!a?.disc, torus: !!a?.torus, moons: a?.moons?.state() ?? [] };
      }),
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const a of this.attached) {
      a.moons?.dispose();
      for (const o of a.own) deferDisposeTree(o);
    }
    this.attached.length = 0;
    this.small?.dispose(); this.small = null;
    this.cometSet?.dispose(); this.cometSet = null;
    this.discs?.dispose(); this.discs = null;
  }
}

export function createWorldsView(env: NaturalEnv, makeup: SystemMakeup): WorldsView | null {
  if (env.flags.unlit) return null; // the legacy unlit sector builds nothing lit
  return new Worlds(env, makeup);
}
